import { Fragment } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { getDocumentById } from '../../../app/helpers/documentationWikiHelper';
import MermaidBlock from './MermaidBlock';

const LinkRenderer = ({ href, children, ...props }) => {
  const doc = href && !href.includes('/') && !href.startsWith('http') && !href.startsWith('#')
    ? getDocumentById(href)
    : null;

  if (doc) {
    return (
      <Link to={`/documentation/${href}`} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
};

const countTableBodyRows = (node) => {
  if (!node?.children) return 0;

  return node.children.reduce((total, section) => {
    if (section.tagName !== 'tbody') return total;
    const rows = section.children?.filter((child) => child.tagName === 'tr').length || 0;
    return total + rows;
  }, 0);
};

const isMermaidPre = (node) => {
  const child = node?.children?.[0];
  const className = child?.properties?.className || [];
  const classes = Array.isArray(className) ? className : [className];
  return classes.some((c) => String(c).includes('language-mermaid'));
};

const markdownComponents = (docId) => ({
  a: LinkRenderer,
  pre: ({ node, children, ...props }) => {
    if (isMermaidPre(node)) {
      return <>{children}</>;
    }

    return (
      <div className="docs-code-wrap">
        <pre {...props}>{children}</pre>
      </div>
    );
  },
  table: ({ node, ...props }) => {
    const rowCount = countTableBodyRows(node);
    const needsPageBreak = rowCount >= 5;

    return (
      <div className={`docs-table-wrap docs-keep-together ${needsPageBreak ? 'docs-table-page-break' : ''}`}>
        <table {...props} />
      </div>
    );
  },
  h2: ({ node, ...props }) => <h2 className="docs-section-heading docs-keep-with-next" {...props} />,
  h3: ({ node, ...props }) => <h3 className="docs-subsection-heading docs-keep-with-next" {...props} />,
  code({ inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const codeText = String(children).replace(/\n$/, '');

    if (!inline && language === 'mermaid') {
      return <MermaidBlock chart={codeText} id={docId || 'doc'} />;
    }

    if (inline) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }

    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
});

const DocsMarkdownRenderer = ({ sections, docId }) => {
  const safeSections = sections && sections.length > 0 ? sections : [''];

  return (
    <div className="docs-markdown-body">
      {safeSections.map((section, index) => (
        <Fragment key={`docs-section-${index}`}>
          {index > 0 && <div className="docs-section-break" aria-hidden="true" />}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents(docId)}
          >
            {section}
          </ReactMarkdown>
        </Fragment>
      ))}
    </div>
  );
};

export default DocsMarkdownRenderer;
